// src/pages/AddContracts.js

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Stack,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';
import contractMock from '../../data/mockContract.json';
import { addContract } from '../../services/contractService';

const AddContracts = () => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [resultData, setResultData] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const navigate = useNavigate();

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
    };

    const transformContract = (data) => {
        const preisdetails = {};
        data.leistungsarten.forEach((art) => {
            art.hmv_nummern.forEach((hmv) => {
                preisdetails[hmv.nummer] = {
                    preis: hmv.preis_euro,
                    typ: art.typ,
                    genehmigungspflichtig: hmv.genehmigungspflichtig || false,
                };
            });
        });
        return { preisdetails };
    };

    const handleUpload = () => {
        setIsLoading(true);
        setProgress(0);
        setResultData(null);
        setUploadError(null);

        const start = Date.now();
        const duration = 2000;
        const interval = setInterval(() => {
            const elapsed = Date.now() - start;
            const pct = Math.min((elapsed / duration) * 100, 97);
            setProgress(pct);
        }, 200);

        setTimeout(() => {
            clearInterval(interval);
            setProgress(100);
            try {
                const parsed = transformContract(contractMock);
                setResultData(parsed);
                addContract(parsed);
            } catch (err) {
                console.error(err);
                setUploadError('Fehler beim Verarbeiten der Datei.');
            } finally {
                setIsLoading(false);
            }
        }, duration);
    };

    const handleRestart = () => {
        setUploadError(null);
        handleUpload();
    };

    const handleReset = () => {
        setSelectedFiles([]);
        setResultData(null);
        setUploadError(null);
        setProgress(0);
    };

    return (
        <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 5 }}>
            <Typography variant="h4" gutterBottom>
                Verträge hochladen
            </Typography>
            <Typography variant="body1" gutterBottom>
                Lade hier PDF-Dateien mit den Hilfsmittelverträgen hoch. Diese werden analysiert und strukturiert gespeichert.
            </Typography>

            {!resultData && !uploadError && (
                <Paper variant="outlined" sx={{ p: 4, mt: 3, textAlign: 'center' }}>
                    <input
                        accept="application/pdf"
                        style={{ display: 'none' }}
                        id="contract-upload"
                        multiple
                        type="file"
                        onChange={handleFileChange}
                    />
                    <label htmlFor="contract-upload">
                        <Button variant="contained" component="span" startIcon={<CloudUploadIcon />}>
                            Dateien auswählen
                        </Button>
                    </label>

                    {selectedFiles.length > 0 && (
                        <Box mt={2}>
                            <Typography variant="body2">{selectedFiles.length} Datei(en) ausgewählt</Typography>
                            <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
                                <Button variant="outlined" onClick={handleReset}>Zurücksetzen</Button>
                                <Button variant="contained" color="primary" onClick={handleUpload} disabled={isLoading}>
                                    Hochladen
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </Paper>
            )}

            {isLoading && (
                <Box mt={4}>
                    <Typography variant="h6" gutterBottom>Vertrag wird analysiert...</Typography>
                    <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />
                </Box>
            )}

            {!isLoading && resultData && (
                <Box mt={5}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                        ✅ Vertrag erfolgreich verarbeitet und in den Speicher übernommen.
                    </Alert>

                    <Typography variant="h6" sx={{ mb: 2 }}>Vertragsdetails:</Typography>

                    <Paper>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Hilfsmittel-Nr.</TableCell>
                                    <TableCell>Preis (€)</TableCell>
                                    <TableCell>Typ</TableCell>
                                    <TableCell>Genehmigungspflichtig</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(resultData.preisdetails || {}).map(([hmv, details]) => (
                                    <TableRow key={hmv}>
                                        <TableCell>{hmv}</TableCell>
                                        <TableCell>{details.preis}</TableCell>
                                        <TableCell>{details.typ}</TableCell>
                                        <TableCell>{details.genehmigungspflichtig ? 'Ja' : 'Nein'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>

                    <Stack direction="row" spacing={2} justifyContent="center" mt={4}>
                        <Button variant="outlined" onClick={handleReset}>Neuen Vertrag hinzufügen</Button>
                        <Button variant="contained" onClick={() => navigate('/')}>Fertig</Button>
                    </Stack>
                </Box>
            )}

            {uploadError && (
                <Box mt={5}>
                    <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>
                    <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
                        <Button variant="outlined" onClick={handleRestart}>Erneut versuchen</Button>
                        <Button variant="contained" onClick={() => navigate('/')}>Abbrechen</Button>
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default AddContracts;